import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import VideoSection from '../components/press/VideoSection';
import BrandAssets from '../components/press/BrandAssets';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import logoHorizontal from '../assets/logo-horizontal.png';

const PressPage = () => {
  const menuItems = [
    { label: 'Features', href: '/#features' },
    { label: 'Blog', href: '/blog' },
    { label: 'Press', href: '/press' },
    { label: 'Pitch', href: '/pitch' },
    { label: 'Enterprise API', href: '/enterprise-api' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const videos = [
    {
      title: 'Product Demo',
      description: 'See Cura\'s platform in action. This comprehensive demo showcases our key features, user interface, and how we\'re streamlining healthcare workflows for better patient outcomes.',
      videoId: 'URRyKcImNns',
    },
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Cura Press & Media – Brand Assets and Resources</title>
        <meta name="description" content="Download brand assets, watch product demos, and access press resources for Cura, the AI-powered medical assistant for seniors ✓" />
        <link rel="canonical" href="https://mycura.org/press" />
        <meta property="og:title" content="Cura Press & Media – Brand Assets and Resources" />
        <meta property="og:description" content="Download brand assets, watch product demos, and access press resources for Cura, the AI-powered medical assistant for seniors." />
        <meta property="og:url" content="https://mycura.org/press" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cura Press & Media – Brand Assets and Resources" />
        <meta name="twitter:description" content="Download brand assets, watch product demos, and access press resources for Cura, the AI-powered medical assistant for seniors." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Cura Press",
            "url": "https://mycura.org/press",
            "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
          })}
        </script>
      </Helmet>
      <Navigation
        menuItems={menuItems}
        scrollToSection={scrollToSection}
        scrollToTop={scrollToTop}
      />

      {/* Header Section */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16 pt-32"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8"
          >
            <img
              src={logoHorizontal}
              alt="Cura Logo"
              className="h-20 mx-auto mb-8"
            />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-4xl md:text-5xl font-light text-gray-800 mb-6"
          >
            Press & Media
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-xl text-gray-700 max-w-3xl mx-auto"
          >
            Resources and assets for journalists, partners, and media professionals
          </motion.p>
        </div>
      </motion.header>

      {/* Video Sections */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-light text-gray-800 mb-4">Featured Videos</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Watch our latest videos to learn more about Cura's mission, products, and team.
            </p>
          </motion.div>

          <div className="space-y-12">
            {videos.map((video, index) => (
              <VideoSection
                key={video.title}
                {...video}
                delay={1.0 + index * 0.2}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Brand Assets Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <BrandAssets />
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PressPage;
